import * as z from 'zod';

import {imageFromSanityImage} from '@/modules/foundation/sanity/image/query';

import {
	embroideryConfigurationFromFormData,
	embroideryConfigurationSchema,
	filterOptionEmbroideriesByBrandIds,
	getConfiguredEmbroidery,
	getDefaultEmbroideryConfiguration,
	getItemsForEmbroideryConfiguration,
} from '../../embroidery-collection/configuration';
import type {ItemForPriceFragment} from '../../item/query';
import type {ProductAdapter} from '../adapter';
import type {ProductDescription} from '../description';
import {resolveProductImageSettings} from '../product-image-settings';
import {type ProductImageSide} from '../ProductImage';
import type {Overlay} from '../ProductImageStack';
import {getOverlayPlacement, type ProductImageStackDescription} from '../ProductImageStack';
import type {UnionProductCoreFragment} from '../query';

import {DOCUMENT_TYPE_CAB_SHOE_TRAY_PRODDUCT} from './schema/names';
import {
	getBaseCabShoeTrayProductBrandsById,
	getFiltersMatchingCabShoeTrayProduct,
} from './filtering';
import type {CabShoeTrayProductCoreFragment, CabShoeTrayProductFragment} from './query';

export const cabShoeTrayConfigurationSchema = z.object({
	embroidery: embroideryConfigurationSchema.optional(),
});

export type CabShoeTrayConfiguration = z.infer<typeof cabShoeTrayConfigurationSchema>;

export function parseCabShoeTrayConfiguration(value: unknown): CabShoeTrayConfiguration | null {
	const result = cabShoeTrayConfigurationSchema.safeParse(value);

	if (!result.success) {
		console.warn('Failed to parse cab shoe tray product configuration', result.error);
		return null;
	}

	return result.data;
}

export function isCabShoeTrayProduct<P extends UnionProductCoreFragment>(
	product: P,
): product is Extract<P, {_type: typeof DOCUMENT_TYPE_CAB_SHOE_TRAY_PRODDUCT}> {
	return product._type === DOCUMENT_TYPE_CAB_SHOE_TRAY_PRODDUCT;
}

export function getFilteredEmbroideryOptionForCabShoeTray(product: CabShoeTrayProductCoreFragment) {
	if (!product.settings?.embroidery) {
		return null;
	}

	// Filter embroideries to only those matching the product's brands:
	const allowedBrandIds = Array.from(getBaseCabShoeTrayProductBrandsById(product).keys());
	return filterOptionEmbroideriesByBrandIds(product.settings.embroidery, allowedBrandIds);
}

// Functions required for product page:

/**
 * Returns the simplest (but still valid) configuration of the product.
 */
export function getDefaultCabShoeTrayConfiguration(
	product: CabShoeTrayProductCoreFragment,
): CabShoeTrayConfiguration {
	const defaultConfig: CabShoeTrayConfiguration = {};

	if (!product.settings) {
		return defaultConfig;
	}

	const embroidery = getFilteredEmbroideryOptionForCabShoeTray(product);
	if (embroidery?.available) {
		defaultConfig.embroidery = getDefaultEmbroideryConfiguration(embroidery);
	}

	// FIXME: Implement!

	return defaultConfig;
}

export function cabShoeTrayConfigurationFromFormData(
	product: CabShoeTrayProductCoreFragment,
	formData: FormData,
): CabShoeTrayConfiguration {
	const config: CabShoeTrayConfiguration = {};

	const embroidery = getFilteredEmbroideryOptionForCabShoeTray(product);
	if (embroidery?.available) {
		config.embroidery = embroideryConfigurationFromFormData(embroidery, 'embroidery', formData);
	}

	return config;
}

export function getItemsForCabShoeTrayConfiguration(
	product: CabShoeTrayProductCoreFragment,
	configuration: CabShoeTrayConfiguration,
): ItemForPriceFragment[] {
	const items: ItemForPriceFragment[] = [];

	// Add base product items:
	items.push(...(product.settings?.items ?? []));

	// Add configured option items:
	const embroidery = getFilteredEmbroideryOptionForCabShoeTray(product);
	if (embroidery?.available && configuration.embroidery) {
		const embroideryItems = getItemsForEmbroideryConfiguration(
			embroidery,
			configuration.embroidery,
		);
		items.push(...embroideryItems);
	}

	// FIXME: Get configured option items

	return items;
}

export type CabShoeTrayConfigurationValidationResult = {
	isValid: boolean;
};

export function validateCabShoeTrayConfiguration(
	_product: CabShoeTrayProductCoreFragment,
	_configuration: CabShoeTrayConfiguration,
): CabShoeTrayConfigurationValidationResult {
	// FIXME: Implement!

	return {isValid: true};
}

export function isValidCabShoeTrayConfiguration(
	product: CabShoeTrayProductCoreFragment,
	configuration: CabShoeTrayConfiguration,
) {
	const validation = validateCabShoeTrayConfiguration(product, configuration);
	return validation.isValid;
}

export function getImageStackForCabShoeTrayConfiguration<Side extends ProductImageSide>(
	side: Side,
	product: CabShoeTrayProductCoreFragment,
	configuration: CabShoeTrayConfiguration | null,
): ProductImageStackDescription | null {
	if (side !== 'top') {
		// For cab shoe trays, we only have a top image:
		return null;
	}

	if (!product.settings) {
		return null;
	}

	// Fetch the most specific (enabled) product image settings:
	const imageSettings = resolveProductImageSettings(product)?.imageSettings;

	if (!imageSettings) {
		return null;
	}

	const baseImage = imageFromSanityImage(imageSettings.baseImage);

	if (!baseImage) {
		// If there's no base image, we don't need to consider the overlays either:
		return null;
	}

	const overlays: Overlay[] = [];

	// Add embroidery overlay, if configured:
	const embroidery = getFilteredEmbroideryOptionForCabShoeTray(product);
	if (embroidery?.available && configuration?.embroidery) {
		const placement = imageSettings.embroideryImagePlacement;

		const {configuredEmbroidery} = getConfiguredEmbroidery(
			embroidery,
			configuration.embroidery,
		);

		if (placement?.isVisible && configuredEmbroidery?.image) {
			const overlayImage = imageFromSanityImage(configuredEmbroidery.image);
			if (overlayImage) {
				overlays.push({
					_type: 'imageOverlay',
					image: overlayImage,
					placement: getOverlayPlacement(placement),
				});
			}
		}
	}

	return {
		image: baseImage,
		altText: null,
		overlays,
	};
}

// Functions required for category pages and cards

export function getProductNameForCabShoeTrayConfiguration(
	product: CabShoeTrayProductCoreFragment,
	_configuration: CabShoeTrayConfiguration,
) {
	return `${product.name}`;
}

export function getDescriptionForCabShoeTrayConfiguration(
	product: CabShoeTrayProductCoreFragment,
	configuration: CabShoeTrayConfiguration,
): ProductDescription {
	const desc: ProductDescription = [];

	const embroidery = getFilteredEmbroideryOptionForCabShoeTray(product);

	if (embroidery?.available && configuration.embroidery) {
		const {configuredCollection, configuredEmbroidery} = getConfiguredEmbroidery(
			embroidery,
			configuration.embroidery,
		);

		if (configuredCollection && configuredEmbroidery) {
			desc.push({
				title: 'Broderi',
				value: [
					{
						title: 'Broderisamling',
						value: configuredCollection.name ?? 'Samling uten navn',
					},
					{
						title: 'Navn',
						value: configuredEmbroidery.name ?? 'Broderi uten navn',
					},
				],
			});
		}
	}

	return desc;
}

export const cabShoeTrayProductAdapter: ProductAdapter<
	CabShoeTrayProductFragment,
	CabShoeTrayProductCoreFragment
> = {
	getName: getProductNameForCabShoeTrayConfiguration,
	getItems: getItemsForCabShoeTrayConfiguration,
	getImages: getImageStackForCabShoeTrayConfiguration,
	getDescription: getDescriptionForCabShoeTrayConfiguration,
	getMatchingFilters: getFiltersMatchingCabShoeTrayProduct,
	isValidConfig: isValidCabShoeTrayConfiguration,
	getDefaultConfig: getDefaultCabShoeTrayConfiguration,
	parseConfigFromFormData: cabShoeTrayConfigurationFromFormData,
	parseConfigFromJSON: parseCabShoeTrayConfiguration,
};
